import type {
	FinancingChange as FinancingProductsSelection,
	FinancingFormProductDetailsView,
	FinancingFormView,
	FinancingView,
} from "@slfinrtl/ui-bff-routes"
import {
	changeFinancingRoute,
	getFinancingFormViewRoute,
	getFinancingProductDetailsViewRoute,
} from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import type { FinancingNotification } from "src/components"
import { FinancingForm, FinancingFormProductDetails, useFinancingNotificationResolver } from "src/components"
import { useBff, useBffDynamic } from "src/hooks"

export interface FinancingFormContainerProps {
	inventoryId: string
	onCancel: () => void
	onSave: (value: FinancingView) => void
}

export const FinancingFormContainer = (props: FinancingFormContainerProps) => {
	const { inventoryId, onCancel } = props
	const [view, setView] = useState<FinancingFormView>()
	const [notification, setNotification] = useState<FinancingNotification>()

	const fetchView = useBff(
		getFinancingFormViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"none",
	)

	const financingNotificationResolver = useFinancingNotificationResolver()
	useEffect(() => {
		async function run() {
			try {
				setNotification(undefined)

				setView(await fetchView())
			} catch (error: unknown) {
				setNotification(financingNotificationResolver(error))
			}
		}

		run()
	}, [fetchView, financingNotificationResolver])

	return notification ? (
		<FinancingForm state="notification" {...notification} onCancel={onCancel} />
	) : !view ? (
		<FinancingForm state="loading" onCancel={onCancel} />
	) : (
		<FinancingFormContainerViewReady {...props} view={view} />
	)
}

interface FinancingFormContainerViewReadyProps extends FinancingFormContainerProps {
	view: FinancingFormView
}

const FinancingFormContainerViewReady = ({
	inventoryId,
	onCancel,
	onSave,
	view,
}: FinancingFormContainerViewReadyProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [selectedProductType, setSelectedProductType] = useState<string>(view.productTypes[0].value)
	const [selectedProducts, setSelectedProducts] = useState<FinancingProductsSelection>(
		view.productTypes.reduce<FinancingProductsSelection>((previous, productType) => {
			return {
				...previous,
				[productType.value]: productType.products.find((it) => it.selected)?.value,
			}
		}, {}),
	)

	const selectedProductTypeViewModel = view.productTypes.find((it) => it.value === selectedProductType)
	if (!selectedProductTypeViewModel) {
		throw new Error(`Couldn't find view model for ${selectedProductType}`)
	}

	const productTypes = view.productTypes.map(({ label, value }) => ({
		label,
		value,
	}))

	// api value is string | null | undefined, but PRadioGroup and PRadioGroupOption needs a string, so we map null and undefined to specific string values
	const products = selectedProductTypeViewModel.products.map((p) => ({ ...p, value: mapValue(p.value) }))
	const product = mapValue(selectedProducts[selectedProductType])
	const isNoProductAvailable = !products.length

	const [productDetailsViewModel, setProductDetailsViewModel] = useState<FinancingFormProductDetailsView | null>()
	const [notification, setNotification] = useState<FinancingNotification>()

	const fetchView = useBff(
		getFinancingProductDetailsViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {
				id: unmapValue(product) ?? undefined, // revert value mapping for api call
				type: selectedProductType,
			},
		},
		"none",
	)

	const saveFinancing = useBffDynamic(changeFinancingRoute, "none")

	const financingNotificationResolver = useFinancingNotificationResolver()
	useEffect(() => {
		let shouldAbort = false
		async function run() {
			setNotification(undefined)

			if (isNoProductAvailable || product === emptyPlaceholder) {
				setProductDetailsViewModel(null)
				return
			}

			setProductDetailsViewModel(undefined)
			try {
				const result = await fetchView()
				if (shouldAbort) {
					return
				}

				setProductDetailsViewModel(result)
			} catch (error: unknown) {
				setNotification(financingNotificationResolver(error))
			}
		}

		run()

		return () => {
			shouldAbort = true
		}
	}, [isNoProductAvailable, selectedProductType, product, fetchView, financingNotificationResolver])

	const handleProductTypeChange = (value: string) => setSelectedProductType(value)
	const handleProductChange = useCallback(
		(value: string) => {
			setSelectedProducts((previous) => ({
				...previous,
				[selectedProductType]: unmapValue(value), // unmap value back to string | null | undefined
			}))
		},
		[selectedProductType],
	)

	const handleSave = () => {
		setIsSubmitting(true)
		return saveFinancing({
			body: selectedProducts,
			pathParams: { inventoryId },
			queryParams: {},
		})
			.then((financingView) => {
				onSave(financingView)
			})
			.finally(() => setIsSubmitting(false))
	}

	return (
		<FinancingForm
			state="ready"
			productTypes={productTypes}
			productType={selectedProductType}
			onProductTypeChange={handleProductTypeChange}
			products={products}
			product={product}
			onProductChange={handleProductChange}
			onCancel={onCancel}
			onSave={handleSave}
			isSubmitting={isSubmitting}
		>
			{notification ? (
				<FinancingFormProductDetails state="notification" {...notification} />
			) : productDetailsViewModel === undefined ? (
				<FinancingFormProductDetails state="loading" />
			) : (
				<FinancingFormProductDetails
					state="ready"
					product={productDetailsViewModel}
					productType={selectedProductType}
				/>
			)}
		</FinancingForm>
	)
}

const mapValue = (value: string | null | undefined): string => {
	if (value === null) {
		return emptyPlaceholder
	}
	return value ?? "auto"
}

const unmapValue = (value: string): string | null | undefined => {
	if (value === emptyPlaceholder) {
		return null
	}
	if (value === "auto") {
		return undefined
	}
	return value
}
